// Shared values and derived logic for the contractor compliance portal — RON-5465.
//
// Kept out of the components so the rules are testable on their own, and so a rule the server owns
// is written down once rather than re-derived in three places.

// ── Document states (§3) ─────────────────────────────────────────────────────────
//
// Carried RAW from the server rather than mapped to a local enum. The portal's whole job is to
// show where a pack stands, and translating the state into our own words would put a layer between
// the contractor and the only thing that tells them what to do next.
// CORRECTED 06-October-2026 against `RON-5465_api-testing-contractor-portal.md` §"What counts as
// outstanding" and the plan's P8. There is no `uploaded` state on the wire: a document that has
// been uploaded is `pending` — the contractor's part is done and it is the reviewer's turn. We had
// invented `uploaded`, which matched nothing the server sends, so a live upload fell through to
// the default tone and was redrawn as a red to-do WITH THE UPLOAD BUTTON STILL ON IT. That is the
// exact failure the contract warns about: "contractors uploading the same file twice."
export const DOC_STATE = {
  AWAITING_SIGN: "awaiting_sign",
  SIGNED: "signed",
  AWAITING_UPLOAD: "awaiting_upload",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  // Outstanding, like `rejected`, but for a different reason and with a different fix: the
  // document was accepted once and has since lapsed. "Not submitted" would be a lie and would send
  // them looking for something they never failed to do.
  EXPIRED: "expired",
};

// How a readable document is acknowledged. `tick` is a checkbox; `esign` wants the name typed.
export const ACK_METHOD = { TICK: "tick", ESIGN: "esign" };

// The scan, which the server stamps and we only report.
//
// CORRECTED 06-October-2026. An earlier note here said uploads sit at `pending` indefinitely
// because the scanner needs a queue worker that was still with devops. That stopped being true on
// 05-October: the scanner is AWS GuardDuty Malware Protection for S3, which scans the object
// itself and posts the verdict back, so there is no job and nothing to queue. Phase 4 shipped.
//
// `pending` is therefore a brief state, not a permanent one — but the screen must still treat it
// as "done, with the reviewer" rather than a to-do, for the reason the contract gives: showing it
// as outstanding has contractors uploading the same file twice.
//
// UPDATED 08-October-2026, from the contract revision of 06-October. `scan: "pending"` is now the
// UNCOMMON case on an upload reply, not the normal one: "GuardDuty is usually finished before the
// portal is even told the file exists, and the upload adopts the verdict from the object's tag."
//
// That matters for one reason: a document can come back **rejected on the upload call itself**,
// not minutes later. The screen must not assume an upload is always followed by a quiet wait —
// it has to redraw from the reply like any other write, which it does, because every write
// returns the whole pack.
//
// `infected` never reaches this screen as a scan state. The server AUTO-REJECTS the document, so
// it comes back as `rejected` with a `rejected_reason` — deliberately flat wording that does not
// name what was found. The file is kept rather than deleted: it is unviewable either way, and it
// is evidence if anyone disputes what was sent.
// `null` when there is no file at all; `error` when the scanner itself failed, which is neither a
// clean bill nor a threat and must not be reported as either.
export const SCAN_STATE = {
  PENDING: "pending",
  CLEAN: "clean",
  INFECTED: "infected",
  ERROR: "error",
};

// ── The four steps ───────────────────────────────────────────────────────────────
export const STEP = { READ: 1, UPLOAD: 2, PERSONNEL: 3, SUBMIT: 4 };

export const STEP_LABEL_KEYS = {
  [STEP.READ]: "react_portal_step_read",
  [STEP.UPLOAD]: "react_portal_step_upload",
  [STEP.PERSONNEL]: "react_portal_step_personnel",
  [STEP.SUBMIT]: "react_portal_step_submit",
};

// A step is done when nothing in it is outstanding. The server counts `outstanding` per step, so
// this reads its answer rather than re-counting the items and risking a different total.
export const isStepDone = (pack, step) => {
  if (!pack) return false;

  if (step === STEP.READ) return (pack.readDocuments?.outstanding ?? 1) === 0;
  if (step === STEP.UPLOAD) return (pack.myDocuments?.outstanding ?? 1) === 0;
  if (step === STEP.PERSONNEL) return pack.personnel?.confirmed === true;

  return pack.submitted === true;
};

// Which step the contractor should be looking at: the first one that is not finished. Used to open
// the right section rather than making them scroll past the parts they have already done.
export const firstOpenStep = (pack) => {
  const order = [STEP.READ, STEP.UPLOAD, STEP.PERSONNEL, STEP.SUBMIT];

  return order.find((step) => !isStepDone(pack, step)) ?? STEP.SUBMIT;
};

// ── Status wording ───────────────────────────────────────────────────────────────
//
// One tone per state, so a badge and a border never disagree about whether something is a problem.
export const DOC_TONE = {
  [DOC_STATE.APPROVED]: "ok",
  [DOC_STATE.SIGNED]: "ok",
  [DOC_STATE.PENDING]: "pending",
  [DOC_STATE.AWAITING_SIGN]: "todo",
  [DOC_STATE.AWAITING_UPLOAD]: "todo",
  [DOC_STATE.REJECTED]: "bad",
  [DOC_STATE.EXPIRED]: "bad",
};

export const getDocTone = (state) => DOC_TONE[state] ?? "todo";

export const DOC_STATE_LABEL_KEYS = {
  [DOC_STATE.APPROVED]: "react_portal_state_approved",
  [DOC_STATE.SIGNED]: "react_portal_state_signed",
  [DOC_STATE.PENDING]: "react_portal_state_pending",
  [DOC_STATE.AWAITING_SIGN]: "react_portal_state_awaiting_sign",
  [DOC_STATE.AWAITING_UPLOAD]: "react_portal_state_awaiting_upload",
  [DOC_STATE.REJECTED]: "react_portal_state_rejected",
  [DOC_STATE.EXPIRED]: "react_portal_state_expired",
};

export const getDocStateLabelKey = (state) =>
  DOC_STATE_LABEL_KEYS[state] ?? "react_portal_state_awaiting_upload";

// ── Formatting ───────────────────────────────────────────────────────────────────

// "2026-05-08" → "Friday 08 May 2026". The portal is read by somebody outside the company on
// their phone, so the day name earns its place: it is what tells them whether it is tomorrow.
export const formatLongDate = (iso, locale = "en-IE") => {
  if (!iso) return "";

  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;

  return parsed.toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// "08:00:00" → "08:00". The seconds are never the point on this screen.
export const formatTime = (value) => {
  if (typeof value !== "string" || value === "") return "";

  const [hours, minutes] = value.split(":");

  return minutes === undefined ? value : `${hours}:${minutes}`;
};

// The file types a given upload accepts, as an `accept` attribute. The server sends "pdf,jpg,png";
// a file input wants ".pdf,.jpg,.png".
export const toAcceptAttribute = (types) =>
  String(types ?? "")
    .split(",")
    .map((type) => type.trim())
    .filter(Boolean)
    .map((type) => `.${type}`)
    .join(",");

// Initials for the contractor's avatar. Two letters, uppercase; a single-word name gives one.
export const getInitials = (name) =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// ── Site access clearance — §6.7, AC-P-67 ────────────────────────────────────────────────────
//
// THE SERVER SENDS THIS NOW. Until 22-October-2026 these four states were DERIVED here, from
// `submitted` and the document rows, because `site_access` did not exist. It does (Phase D), and
// the Laravel team were blunt about the general case: *"We send it; please do not derive it."*
//
// The derivation is gone rather than kept as a fallback. A fallback would be a second opinion
// that nobody reads until the day it disagrees — and the disagreement here is a contractor being
// told they are cleared when the server says they are not. The same argument this module already
// makes about `submit.outstanding` and about `outstanding` on a document row.
//
// What survived the deletion is the ORDER and the tone mapping, because those are still ours to
// get right: red beats amber beats green, because the banner exists to tell somebody they will
// be turned away at the door.
export const CLEARANCE = {
  NOT_YET: "not_cleared_yet",
  BLOCKED: "not_cleared",
  AWAITING: "awaiting_approval",
  CLEARED: "cleared",
};

// Renewal, as a state of its own on top of `cleared` (AC-P-76, AC-P-78). A renewal does not
// change clearance — the current version stays valid — so this never turns the banner red.
export const RENEWAL = {
  AWAITING: "awaiting_approval",
  NEEDS_ACTION: "needs_action",
};

const CLEARANCE_TONES = {
  [CLEARANCE.NOT_YET]: "warn",
  [CLEARANCE.BLOCKED]: "bad",
  [CLEARANCE.AWAITING]: "warn",
  [CLEARANCE.CLEARED]: "good",
};

// The one piece of judgement left: `not_cleared_yet` goes RED when something already needs
// action, so a contractor sees the problem before they press submit rather than after. §6.7's
// own parenthesis — "(red if `needs_action` > 0)".
//
// And a CLEARED pack whose renewal needs action goes amber: they are on site today, and they
// will not be after `renewal_until` unless they act.
export const getClearanceTone = (siteAccess) => {
  if (!siteAccess) return "warn";

  if (siteAccess.state === CLEARANCE.NOT_YET && siteAccess.needsAction > 0) return "bad";

  if (siteAccess.state === CLEARANCE.CLEARED && siteAccess.renewal === RENEWAL.NEEDS_ACTION) {
    return "warn";
  }

  return CLEARANCE_TONES[siteAccess.state] ?? "warn";
};

const CLEARANCE_TITLE_KEYS = {
  [CLEARANCE.NOT_YET]: "react_portal_clearance_not_yet",
  [CLEARANCE.BLOCKED]: "react_portal_clearance_blocked",
  [CLEARANCE.AWAITING]: "react_portal_clearance_awaiting",
  [CLEARANCE.CLEARED]: "react_portal_clearance_cleared",
};

export const getClearanceTitleKey = (state) => CLEARANCE_TITLE_KEYS[state] ?? "";

// Where a write should leave the contractor.
//
// CHANGED 21-October-2026, with the ungating. `CompliancePortalUI` used to call
// `setOpenStep(firstOpenStep(next))` after every successful write, unconditionally. That was
// harmless while steps were gated — the only step you could be on WAS the first open one — and
// became wrong the moment any step could be opened in any order.
//
// The case it breaks: a contractor opens step 2 to upload an insurance certificate while step 1
// is still unfinished. The upload succeeds, step 2 is still not complete because a second
// document is outstanding — and the old rule would throw them back to step 1 anyway, so the next
// file needs them to navigate back.
//
// So: move on only when the step they are on is FINISHED. Otherwise leave them where they chose
// to be, which is the entire point of ungating.
//
// Here rather than inline in the component because the two branches need different packs to
// exercise, and the fixture only produces one of them.
export const nextOpenStep = (pack, current) =>
  isStepDone(pack, current) ? firstOpenStep(pack) : current;

// ── Replacing a document — Phase C (AC-P-61 to AC-P-68) ──────────────────────────────────────
//
// What the upload button says, keyed on the server's `upload_action`. The five labels are
// CHANGES §1's, and each names the thing that is actually happening — which matters more here
// than anywhere else on this screen, because "Replace document" on an approved file withdraws a
// clearance the contractor already has.
export const UPLOAD_ACTION = {
  UPLOAD: "upload",
  UPLOAD_CORRECTED: "upload_corrected",
  REPLACE_FILE: "replace_file",
  REPLACE_DOCUMENT: "replace_document",
  UPLOAD_RENEWED: "upload_renewed",
};

const UPLOAD_ACTION_LABEL_KEYS = {
  [UPLOAD_ACTION.UPLOAD]: "react_portal_upload_choose",
  [UPLOAD_ACTION.UPLOAD_CORRECTED]: "react_portal_upload_corrected",
  [UPLOAD_ACTION.REPLACE_FILE]: "react_portal_replace_file",
  [UPLOAD_ACTION.REPLACE_DOCUMENT]: "react_portal_replace_document",
  [UPLOAD_ACTION.UPLOAD_RENEWED]: "react_portal_upload_renewed",
};

// Falls back to the plain "choose a file" rather than an empty button. An action we have not
// seen is still an action the server says is allowed.
export const getUploadActionLabelKey = (action) =>
  UPLOAD_ACTION_LABEL_KEYS[action] ?? "react_portal_upload_choose";

// What has to be said before the upload area opens.
//
// `withdraws_approval` is AC-P-62: replacing an approved document withdraws the approval, the
// contractor is not cleared until the new file is approved, and they cannot check in if it is
// not approved by the time they arrive. That is three consequences, and none of them is
// recoverable by pressing Cancel afterwards — so it is said first, with a way out.
export const REPLACE_WARNING = {
  WITHDRAWS_APPROVAL: "withdraws_approval",
  // Phase D. Handled with the renewal work; listed here so the vocabulary is in one place.
  RENEWAL_POSSIBLE: "renewal_possible",
};

// AC-P-63: choosing "Replace anyway" and then NOT uploading leaves the document exactly as it
// was. So the warning is a gate in front of the upload area, not a state change — nothing is
// sent until a file is actually chosen.
export const needsReplaceWarning = (doc) =>
  doc?.replaceWarning === REPLACE_WARNING.WITHDRAWS_APPROVAL;

// ── The pack's mode — Phase E (AC-P-70 to AC-P-80) ───────────────────────────────────────────
//
// Two kinds, and BOTH are vetting cycles. There is no "visit" mode: the 15-October revision
// removed it, and the prototype carries the reason in its own comment — "vetting is decoupled
// from visits". A pack belongs to a contractor, not to a booking.
export const MODE_KIND = {
  PREAPPROVAL: "preapproval",
  RENEWAL: "renewal",
};

export const isRenewalPack = (pack) => pack?.mode?.kind === MODE_KIND.RENEWAL;

// The state of a renewal already uploaded (AC-P-76, AC-P-78).
export const RENEWAL_STATE = {
  // With the reviewer. The current version is still valid, so there is nothing to do.
  AWAITING: "awaiting_approval",
  // Rejected. They have to try again, and the clock is the old version's expiry.
  NEEDS_ACTION: "needs_action",
};

// AC-P-80: on an approved document WITH an expiry, the renewal explanation comes FIRST — "stays
// valid until [date], the new date applies once approved" — and only then the general warning
// that a replacement withdraws the approval.
//
// The order matters because the two say opposite things about the same button. A contractor who
// reads only the general warning will not renew a document they should renew.
export const explainsRenewalFirst = (doc) =>
  doc?.replaceWarning === REPLACE_WARNING.RENEWAL_POSSIBLE;
